import { UserRoleOrUserPermission } from '..';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { FORM_MODE } from '@savgroup-front-common/constants/control/formMode';
import { useBanners } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { AssignedSites } from '@savgroup-front-common/types';
import { AuthorizationService } from '@/api';

import { setUserActionCreator } from '../actionCreators';

interface UseUpsertUserArgs {
  email: string;
  lastname: string;
  firstname: string;
  concernedEntities: string[];
  credentials: UserRoleOrUserPermission[];
  profiles: { sellerId: string; userProfileIds: string[] }[];
  type: FORM_MODE;
  assignedSites?: AssignedSites;
  stocks?: { stockId: string; stockName: string }[];
}
type UseSetUserReturnValue = {
  userId?: string;
  isFailure: boolean;
};

const useUpsertUser = () => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { pushErrors } = useBanners();

  const handleUpsertUser = useCallback(
    async ({
      email,
      lastname,
      firstname,
      concernedEntities,
      credentials,
      profiles,
      type,
      assignedSites,
      stocks,
    }: UseUpsertUserArgs): Promise<UseSetUserReturnValue> => {
      setIsLoading(true);
      let isFailure = false;

      const userResponse = await AuthorizationService.setUser({
        email,
        lastname,
        firstname,
        concernedEntities,
        credentials,
        assignedSites,
        stocks,
      });

      if (userResponse.failure) {
        pushErrors(userResponse.errors);

        setIsLoading(false);

        return {
          userId: undefined,
          isFailure: true,
        };
      }

      const userId = userResponse.value;

      for (let index = 0; index < profiles.length; index += 1) {
        const profile = profiles[index];

        const response = await AuthorizationService.setUserProfilesToUser({
          userProfileIds: profile.userProfileIds,
          sellerId: profile.sellerId,
          userId,
        });

        if (response.failure) {
          isFailure = true;
          pushErrors(response.errors);
        }
      }

      dispatch(
        setUserActionCreator({
          userId,
          email,
          lastname,
          firstname,
          credentials,
          type,
        }),
      );

      setIsLoading(false);

      return {
        userId,
        isFailure,
      };
    },
    [dispatch, pushErrors],
  );

  return {
    handleUpsertUser,
    isLoading,
  };
};

export default useUpsertUser;
